import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CalendarDays, Clock, CheckCircle, X, ArrowLeft } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, parseISO, isSameDay } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import type { User as SupabaseUser, Session } from "@supabase/supabase-js";

type EntityType = "seu_ze" | "seu_lucifer";

interface AppointmentSlot {
  id: string;
  slot_date: string;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  entity_type: EntityType;
}

interface Appointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  status: string;
  entity_type: EntityType;
}

const ENTITY_NAMES: Record<EntityType, { pt: string; en: string; es: string }> = {
  seu_ze: { pt: "Seu Zé Pelintra", en: "Seu Zé Pelintra", es: "Seu Zé Pelintra" },
  seu_lucifer: { pt: "Seu Lúcifer", en: "Seu Lúcifer", es: "Seu Lúcifer" },
};

const Agendamento = () => {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [userAppointments, setUserAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<EntityType | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get entity name based on current language
  const getEntityName = (entity: EntityType) => {
    const lang = i18n.language.startsWith("en") ? "en" : i18n.language.startsWith("es") ? "es" : "pt";
    return ENTITY_NAMES[entity][lang];
  };

  // Get the appropriate date-fns locale based on current language
  const getDateLocale = () => {
    const lang = i18n.language;
    if (lang === 'en' || lang.startsWith('en-')) return enUS;
    if (lang === 'es' || lang.startsWith('es-')) return es;
    return ptBR;
  };

  const dateLocale = getDateLocale();

  const openCancelDialog = (appointmentId: string) => {
    setAppointmentToCancel(appointmentId);
    setCancelDialogOpen(true);
  };

  const confirmCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    await handleCancelAppointment(appointmentToCancel);
    setCancelDialogOpen(false);
    setAppointmentToCancel(null);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (!session) {
        navigate("/auth");
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (!session) {
        navigate("/auth");
      } else {
        fetchData(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchData = async (userId: string) => {
    setIsLoading(true);
    try {
      // Fetch active slots - use local timezone for consistent date comparison
      const todayLocal = format(new Date(), "yyyy-MM-dd");
      const { data: slotsData } = await supabase
        .from("appointment_slots")
        .select("*")
        .eq("is_active", true)
        .gte("slot_date", todayLocal);

      // Fetch all appointments for those slots
      const { data: appointmentsData } = await supabase
        .from("appointments")
        .select("*")
        .neq("status", "cancelled");

      // Fetch user's appointments
      const { data: userApptsData } = await supabase
        .from("appointments")
        .select("*")
        .eq("user_id", userId)
        .neq("status", "cancelled");

      // Cast entity_type
      const slotsWithEntity = (slotsData || []).map((slot) => ({
        ...slot,
        entity_type: (slot.entity_type as EntityType) || "seu_ze",
      }));

      const appointmentsWithEntity = (appointmentsData || []).map((appt) => ({
        ...appt,
        entity_type: (appt.entity_type as EntityType) || "seu_ze",
      }));

      const userApptsWithEntity = (userApptsData || []).map((appt) => ({
        ...appt,
        entity_type: (appt.entity_type as EntityType) || "seu_ze",
      }));

      setSlots(slotsWithEntity);
      setAppointments(appointmentsWithEntity);
      setUserAppointments(userApptsWithEntity);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter slots by selected entity (memoized)
  const filteredSlots = useMemo(
    () => (selectedEntity ? slots.filter((slot) => slot.entity_type === selectedEntity) : []),
    [slots, selectedEntity]
  );

  // Get dates that have available slots for selected entity
  const availableDates = filteredSlots.map((slot) => parseISO(slot.slot_date));

  // Memoize available times calculation to prevent flickering
  const calculatedAvailableTimes = useMemo(() => {
    if (!selectedDate || !selectedEntity) return [];

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const slotForDate = filteredSlots.find((s) => s.slot_date === dateStr);

    if (!slotForDate) return [];

    // Generate time slots
    const times: string[] = [];
    const startParts = slotForDate.start_time.split(":");
    const endParts = slotForDate.end_time.split(":");
    const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
    const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
    const duration = slotForDate.slot_duration_minutes;

    for (let m = startMinutes; m < endMinutes; m += duration) {
      const hours = Math.floor(m / 60);
      const mins = m % 60;
      times.push(`${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`);
    }

    // Filter out already booked times
    const bookedTimes = appointments
      .filter((a) => a.appointment_date === dateStr && a.entity_type === selectedEntity)
      .map((a) => a.appointment_time.slice(0, 5));

    return times.filter((t) => !bookedTimes.includes(t));
  }, [selectedDate, selectedEntity, filteredSlots, appointments]);

  // Update availableTimes only when calculated times change
  useEffect(() => {
    setAvailableTimes(calculatedAvailableTimes);
  }, [calculatedAvailableTimes]);

  // Reset selected time when date or entity changes
  useEffect(() => {
    setSelectedTime(null);
  }, [selectedDate, selectedEntity]);

  // Check if user already has an appointment on this date for this entity
  const hasAppointmentOnDate = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return userAppointments.some((a) => a.appointment_date === dateStr && a.entity_type === selectedEntity);
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    if (!user) return;
    
    try {
      // First, call the edge function to delete from Google Calendar
      const { data: cancelResult, error: cancelError } = await supabase.functions.invoke(
        "cancel-appointment",
        { body: { appointment_id: appointmentId } }
      );

      if (cancelError) {
        console.error("Error calling cancel-appointment:", cancelError);
        // Continue anyway - we should still update the DB
      } else {
        console.log("Cancel result:", cancelResult);
      }

      // Then update the status in the database
      const { error } = await supabase
        .from("appointments")
        .update({ status: "cancelled" })
        .eq("id", appointmentId)
        .eq("user_id", user.id);

      if (error) throw error;

      toast({
        title: t("appointment.toast.cancelledTitle"),
        description: cancelResult?.calendarDeleted 
          ? t("appointment.toast.cancelledDesc")
          : t("appointment.toast.cancelledDescSimple"),
      });

      await fetchData(user.id);
    } catch (error: any) {
      toast({
        title: t("appointment.toast.cancelError"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Format date for display based on locale
  const formatDateDisplay = (date: Date, formatStr: string = "dd 'de' MMMM") => {
    // Use locale-appropriate format
    if (i18n.language === 'en' || i18n.language.startsWith('en-')) {
      return format(date, "MMMM dd", { locale: enUS });
    }
    return format(date, formatStr, { locale: dateLocale });
  };

  const formatDateDisplayFull = (date: Date) => {
    if (i18n.language === 'en' || i18n.language.startsWith('en-')) {
      return format(date, "MMMM dd, yyyy", { locale: enUS });
    }
    if (i18n.language === 'es' || i18n.language.startsWith('es-')) {
      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: es });
    }
    return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const handleBookAppointment = async () => {
    if (!selectedDate || !selectedTime || !user || !session || !selectedEntity) return;

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const slotForDate = filteredSlots.find((s) => s.slot_date === dateStr);

    if (!slotForDate) return;

    // Check if user already has an appointment on this date for this entity
    if (hasAppointmentOnDate(selectedDate)) {
      toast({
        title: t("appointment.toast.limitTitle"),
        description: t("appointment.toast.limitDesc"),
        variant: "destructive",
      });
      return;
    }

    setBooking(true);

    try {
      // First, create the appointment in the database
      const { data: appointmentData, error } = await supabase.from("appointments").insert({
        slot_id: slotForDate.id,
        user_id: user.id,
        appointment_date: dateStr,
        appointment_time: selectedTime + ":00",
        status: "confirmed",
        entity_type: selectedEntity,
      }).select().single();

      if (error) throw error;

      // Then call the edge function to create Google Calendar event and send email
      try {
        const { data: integrationResult, error: integrationError } = await supabase.functions.invoke(
          "create-appointment",
          {
            body: {
              appointment_id: appointmentData.id,
              appointment_date: dateStr,
              appointment_time: selectedTime + ":00",
              entity_type: selectedEntity,
            },
          }
        );

        if (integrationError) {
          console.error("Integration error:", integrationError);
          // Don't throw - appointment was created successfully
        } else {
          console.log("Integration result:", integrationResult);
        }

        const dateDisplay = formatDateDisplay(selectedDate);
        
        toast({
          title: t("appointment.toast.successTitle"),
          description: integrationResult?.emailSent 
            ? t("appointment.toast.successDesc", { date: dateDisplay, time: selectedTime })
            : t("appointment.toast.successDescSimple", { date: dateDisplay, time: selectedTime }),
        });
      } catch (funcError) {
        console.error("Edge function error:", funcError);
        const dateDisplay = formatDateDisplay(selectedDate);
        // Still show success since appointment was created
        toast({
          title: t("appointment.toast.successTitle"),
          description: t("appointment.toast.successDescSimple", { date: dateDisplay, time: selectedTime }),
        });
      }

      // Refresh data
      await fetchData(user.id);
      setSelectedDate(undefined);
      setSelectedTime(null);
    } catch (error: any) {
      toast({
        title: t("appointment.toast.bookingError"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setBooking(false);
    }
  };

  const formatTime12h = (time: string) => {
    const [hours, mins] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${mins} ${ampm}`;
  };

  const handleBackToEntitySelection = () => {
    setSelectedEntity(null);
    setSelectedDate(undefined);
    setSelectedTime(null);
    setAvailableTimes([]);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={t("appointment.seoTitle")}
        description={t("appointment.seoDescription")}
      />
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="font-display text-3xl md:text-4xl text-primary font-bold">
                {t("appointment.title")}
              </h1>
              <p className="text-muted-foreground mt-2">
                {t("appointment.subtitle")}
              </p>
            </div>

            {/* User's existing appointments */}
            {userAppointments.length > 0 && (
              <Card className="mb-8 border-2 border-accent/30">
                <CardHeader>
                  <CardTitle className="text-xl font-display text-primary flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    {t("appointment.yourAppointments")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {userAppointments.map((appt) => (
                      <div
                        key={appt.id}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <CalendarDays className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-medium">
                              {formatDateDisplayFull(parseISO(appt.appointment_date))}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatTime12h(appt.appointment_time.slice(0, 5))} - {getEntityName(appt.entity_type)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="px-3 py-1 bg-accent/20 text-accent-foreground rounded-full text-sm font-medium">
                            {appt.status === "confirmed" ? t("appointment.confirmed") : appt.status}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => openCancelDialog(appt.id)}
                          >
                            <X className="w-4 h-4 mr-1" />
                            {t("appointment.cancel")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cancel Confirmation Dialog */}
            <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("appointment.cancelDialog.title")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("appointment.cancelDialog.description")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("appointment.cancelDialog.back")}</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={confirmCancelAppointment}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t("appointment.cancelDialog.confirm")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Entity Selection */}
            {!selectedEntity && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="text-xl font-display text-primary">
                    {t("appointment.selectEntity.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("appointment.selectEntity.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card 
                      className="cursor-pointer border-2 hover:border-primary/50 transition-all hover:shadow-lg"
                      onClick={() => setSelectedEntity("seu_ze")}
                    >
                      <CardHeader className="text-center">
                        <CardTitle className="text-2xl font-display text-primary">
                          {getEntityName("seu_ze")}
                        </CardTitle>
                        <CardDescription>
                          {t("appointment.entities.seu_ze.description")}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                    <Card 
                      className="cursor-pointer border-2 hover:border-primary/50 transition-all hover:shadow-lg"
                      onClick={() => setSelectedEntity("seu_lucifer")}
                    >
                      <CardHeader className="text-center">
                        <CardTitle className="text-2xl font-display text-primary">
                          {getEntityName("seu_lucifer")}
                        </CardTitle>
                        <CardDescription>
                          {t("appointment.entities.seu_lucifer.description")}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Slots Available Message */}
            {selectedEntity && filteredSlots.length === 0 && (
              <Card className="mb-8 border-2 border-amber-500/30 bg-amber-50/50 dark:bg-amber-900/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-display text-amber-700 dark:text-amber-500 flex items-center gap-2">
                      <CalendarDays className="w-5 h-5" />
                      {t("appointment.noAvailability.title")}
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={handleBackToEntitySelection}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {t("appointment.backToEntities")}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-700 dark:text-amber-400">
                    {t("appointment.noAvailability.messageForEntity", { entity: getEntityName(selectedEntity) })}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Booking Section */}
            {selectedEntity && filteredSlots.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-display text-primary font-bold">
                    {t("appointment.bookingFor")} {getEntityName(selectedEntity)}
                  </h2>
                  <Button variant="outline" size="sm" onClick={handleBackToEntitySelection}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t("appointment.backToEntities")}
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Calendar */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl font-display text-primary flex items-center gap-2">
                        <CalendarDays className="w-5 h-5" />
                        {t("appointment.selectDate.title")}
                      </CardTitle>
                      <CardDescription>
                        {t("appointment.selectDate.hint")}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        locale={dateLocale}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          if (date < today) return true;
                          return !availableDates.some((d) => isSameDay(d, date));
                        }}
                        modifiers={{
                          available: availableDates,
                        }}
                        modifiersStyles={{
                          available: {
                            backgroundColor: "hsl(142 76% 36%)",
                            color: "white",
                            fontWeight: "bold",
                            borderRadius: "6px",
                          },
                        }}
                        classNames={{
                          months: "flex flex-col w-full",
                          month: "space-y-4 w-full",
                          table: "w-full border-collapse",
                          head_row: "flex w-full justify-between",
                          head_cell: "text-foreground/70 rounded-md flex-1 font-medium text-sm text-center",
                          row: "flex w-full mt-2 justify-between",
                          cell: "flex-1 h-10 text-center text-sm p-0 relative",
                          day: "h-10 w-full p-0 font-normal hover:bg-muted rounded-md",
                          day_disabled: "text-foreground/50 cursor-not-allowed",
                          day_outside: "text-foreground/30",
                        }}
                        className="rounded-md border pointer-events-auto w-full p-4"
                      />
                    </CardContent>
                  </Card>

                  {/* Time Selection & Confirmation */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl font-display text-primary flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        {t("appointment.availableTimes.title")}
                      </CardTitle>
                      <CardDescription>
                        {selectedDate
                          ? t("appointment.availableTimes.forDate", { date: formatDateDisplay(selectedDate) })
                          : t("appointment.availableTimes.selectDateFirst")}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {!selectedDate ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>{t("appointment.availableTimes.selectDateCalendar")}</p>
                        </div>
                      ) : availableTimes.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>{t("appointment.availableTimes.noTimesAvailable")}</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            {availableTimes.map((time) => (
                              <Button
                                key={time}
                                variant={selectedTime === time ? "default" : "outline"}
                                className="w-full"
                                onClick={() => setSelectedTime(time)}
                              >
                                {formatTime12h(time)}
                              </Button>
                            ))}
                          </div>

                          {selectedTime && (
                            <div className="mt-6 p-4 bg-muted/50 rounded-lg space-y-3">
                              <h4 className="font-display text-lg text-primary font-semibold">
                                {t("appointment.summary.title")}
                              </h4>
                              <div className="space-y-2 text-sm">
                                <p>
                                  <strong>{t("appointment.summary.entity")}</strong>{" "}
                                  {getEntityName(selectedEntity)}
                                </p>
                                <p>
                                  <strong>{t("appointment.summary.date")}</strong>{" "}
                                  {formatDateDisplayFull(selectedDate)}
                                </p>
                                <p>
                                  <strong>{t("appointment.summary.time")}</strong> {formatTime12h(selectedTime)}
                                </p>
                                <p>
                                  <strong>{t("appointment.summary.price")}</strong> {t("appointment.summary.cash")}
                                </p>
                                <p>
                                  <strong>{t("appointment.summary.duration")}</strong> {t("appointment.summary.hour")}
                                </p>
                              </div>
                              <Button
                                className="w-full mt-4"
                                onClick={handleBookAppointment}
                                disabled={booking}
                              >
                                {booking ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    {t("appointment.booking")}
                                  </>
                                ) : (
                                  t("appointment.confirmBooking")
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {/* Instructions */}
            <Card className="mt-8 border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl font-display text-primary">
                  {t("appointment.importantInfo.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-foreground/80">
                <p>
                  <strong>{t("appointment.importantInfo.priceLabel")}</strong> {t("appointment.importantInfo.priceValue")}
                </p>
                <p>
                  <strong>{t("appointment.importantInfo.arrivalLabel")}</strong> {t("appointment.importantInfo.arrivalValue")}
                </p>
                <p>
                  <strong>{t("appointment.importantInfo.clothingLabel")}</strong> {t("appointment.importantInfo.clothingValue")}
                </p>
                <p>
                  <strong>{t("appointment.importantInfo.locationLabel")}</strong> {t("appointment.importantInfo.locationName")}
                  <br />
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=11631+Black+Rail+St+Windermere+FL+34786" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    11631 Black Rail St, Windermere, FL 34786
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Agendamento;
